import React, { useState, useEffect, useContext } from 'react';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import { api } from 'common';
import { colors } from 'components/Theme/WebTheme';
import { ThemeContext } from '../components/Theme/ThemeContext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

const SegmentedControl = ({ options, value, onChange, isDark }) => {
    return (
        <div style={{
            display: 'flex',
            background: isDark ? 'rgba(15, 23, 42, 0.4)' : 'rgba(241, 245, 249, 0.8)',
            padding: '3px',
            borderRadius: '10px',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.05)',
        }}>
            {options.map((opt) => {
                const isActive = value === opt.value;
                return (
                    <button
                        key={opt.value}
                        onClick={() => onChange(opt.value)}
                        style={{
                            padding: '4px 12px',
                            borderRadius: '8px',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: isActive ? 600 : 500,
                            backgroundColor: isActive ? '#E9AE31' : 'transparent',
                            color: isActive ? '#000000' : (isDark ? 'rgba(255, 255, 255, 0.6)' : '#64748b'),
                            cursor: 'pointer',
                            boxShadow: isActive ? '0 2px 4px rgba(233, 174, 49, 0.2)' : 'none',
                            transition: 'all 0.2s ease',
                            transform: 'none',
                            outline: 'none'
                        }}
                    >
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
};

const CustomSelect = ({ value, onChange, options, isDark, cardBorder }) => {
    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <select
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                style={{
                    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.8)' : '#ffffff',
                    color: isDark ? '#ffffff' : '#0f172a',
                    border: cardBorder,
                    borderRadius: '10px',
                    padding: '6px 32px 6px 12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    outline: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none',
                    minWidth: '90px',
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='${isDark ? '%23ffffff' : '%230f172a'}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 10px center',
                    backgroundSize: '14px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
            >
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value} style={{ background: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#ffffff' : '#0f172a' }}>
                        {opt.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

    const [settings, setSettings] = useState({});
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const { fetchDrivers, clearFetchDrivers } = api;

    const { isDark } = useContext(ThemeContext);
    const [trendChartType, setTrendChartType] = useState('area');
    const [ratioChartType, setRatioChartType] = useState('donut');

    const currentTheme = {
        background: isDark ? '#000000' : '#ffffff',
        cardBg: isDark ? '#111215' : '#ffffff',
        cardBorder: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
        textMain: isDark ? '#ffffff' : '#0f172a',
        textMuted: isDark ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
        shadow: isDark ? '0 4px 6px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.05)',
        mapStyles: isDark ? [
            { "elementType": "geometry", "stylers": [{ "color": "#242f3e" }] },
            { "elementType": "labels.text.stroke", "stylers": [{ "color": "#242f3e" }] },
            { "elementType": "labels.text.fill", "stylers": [{ "color": "#746855" }] },
            { "featureType": "administrative.locality", "elementType": "labels.text.fill", "stylers": [{ "color": "#d59563" }] },
            { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#d59563" }] },
            { "featureType": "poi.park", "elementType": "geometry", "stylers": [{ "color": "#263c3f" }] },
            { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#6b9a76" }] },
            { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#38414e" }] },
            { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#212a37" }] },
            { "featureType": "road", "elementType": "labels.text.fill", "stylers": [{ "color": "#9ca5b3" }] },
            { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#746855" }] },
            { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#1f2835" }] },
            { "featureType": "road.highway", "elementType": "labels.text.fill", "stylers": [{ "color": "#f3d19c" }] },
            { "featureType": "transit", "elementType": "geometry", "stylers": [{ "color": "#2f3948" }] },
            { "featureType": "transit.station", "elementType": "labels.text.fill", "stylers": [{ "color": "#d59563" }] },
            { "featureType": "water", "elementType": "geometry", "stylers": [{ "color": "#17263c" }] },
            { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#515c6d" }] },
            { "featureType": "water", "elementType": "labels.text.stroke", "stylers": [{ "color": "#17263c" }] }
        ] : []
    };

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];

            for (let i = 0; i < drivers.length; i++) {
                if (drivers[i].location) {
                    let carImage = 'https://cdn.pixabay.com/photo/2012/04/15/22/09/car-35502__480.png';
                    let bookingRef = null;
                    for (let j = 0; j < cars.length; j++) {
                        if (cars[j].name === drivers[i].carType) {
                            carImage = cars[j].image;
                        }
                    }
                    for (let j = 0; j < liveBookings.length; j++) {
                        if (liveBookings[j].driver === drivers[i].uid) {
                            bookingRef = liveBookings[j].reference;
                        }
                    }
                    locs.push({
                        id: i,
                        lat: drivers[i].location.lat,
                        lng: drivers[i].location.lng,
                        drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                        carnumber: drivers[i].vehicleNumber,
                        cartype: drivers[i].carType,
                        carImage: carImage,
                        bookingRef: bookingRef,
                        fleetadmin: drivers[i].fleetadmin
                    });
                }
            }
            if (auth.profile.usertype === 'fleetadmin') {
                locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
            }
            setLocations(locs);

        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);
    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || auth.profile.usertype === 'admin')) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - parseFloat(discount);
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings, auth.profile.uid, auth.profile.usertype]);

    const barChartOptions = {
        chart: {
            type: trendChartType,
            height: 350,
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: 'Inter, sans-serif'
        },
        colors: [colors.GREEN, colors.RED],
        dataLabels: { enabled: false },
        stroke: {
            curve: 'smooth',
            width: 3.5,
            lineCap: 'round'
        },
        fill: trendChartType === 'area' ? {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [0, 100]
            }
        } : {
            type: 'solid',
            opacity: 1
        },
        grid: {
            show: true,
            borderColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
            strokeDashArray: 4,
            xaxis: {
                lines: { show: false }
            },
            padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 10
            }
        },
        xaxis: {
            categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: {
                style: {
                    colors: isDark ? '#94a3b8' : '#64748b',
                    fontSize: '12px',
                    fontFamily: 'Inter, sans-serif'
                }
            },
            tooltip: { enabled: false }
        },
        yaxis: {
            labels: {
                style: {
                    colors: isDark ? '#94a3b8' : '#64748b',
                    fontSize: '12px',
                    fontFamily: 'Inter, sans-serif'
                }
            }
        },
        legend: {
            labels: {
                colors: isDark ? '#e2e8f0' : '#0f172a'
            }
        },
        markers: {
            size: 0,
            hover: { size: 6 }
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            x: { show: false }
        }
    };

    const barChart = [{
        name: t('completed_bookings'),
        data: allCompleteCount
    }, {
        name: t('cancelled_bookings'),
        data: allCancelCount
    }];

    let completedMonthCount = 0;
    let cancelledMonthCount = 0;
    if (bookinglistdata.bookings) {
        for (let j = 0; j < bookinglistdata.bookings.length; j++) {
            const { tripdate, status } = bookinglistdata.bookings[j];
            let tDate = new Date(tripdate);
            if (tDate.getMonth() === selectedMonth) {
                if (status === 'PAID' || status === 'COMPLETE') {
                    completedMonthCount++;
                } else if (status === 'CANCELLED') {
                    cancelledMonthCount++;
                }
            }
        }
    }
    const donutSeries = [completedMonthCount, cancelledMonthCount];

    const donutChartOptions = {
        chart: {
            type: ratioChartType,
            fontFamily: 'Inter, sans-serif'
        },
        labels: [t('completed_bookings'), t('cancelled_bookings')],
        colors: [colors.GREEN, colors.RED],
        legend: {
            position: 'bottom',
            labels: {
                colors: isDark ? '#e2e8f0' : '#0f172a'
            }
        },
        dataLabels: {
            enabled: true,
            formatter: function (val) {
                return val.toFixed(1) + "%";
            }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: ratioChartType === 'donut',
                        name: {
                            show: true,
                            fontSize: '14px',
                            color: isDark ? '#94a3b8' : '#64748b'
                        },
                        value: {
                            show: true,
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: isDark ? '#ffffff' : '#0f172a',
                            formatter: function (val) {
                                return val;
                            }
                        },
                        total: {
                            show: true,
                            label: t('total'),
                            color: isDark ? '#94a3b8' : '#64748b',
                            formatter: function (w) {
                                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                            }
                        }
                    }
                }
            }
        },
        tooltip: {
            theme: isDark ? 'dark' : 'light'
        }
    };
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                    }
                }
                if (usersdata.users[i].driverActiveStatus === true) {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        activeCount = activeCount + 1;
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0)
            setDriverCount(0)
            setCustomerCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        let allCompleteCount = [];
        let allCancelCount = [];
        let months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        if (bookinglistdata.bookings) {
            for (let i = 0; i < months.length; i++) {
                let completeCount = 0;
                let cancelCount = 0;
                for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                    const { tripdate } = bookinglistdata.bookings[j];
                    let tDate = new Date(tripdate);
                    if (months[i] === months[tDate.getMonth()] && (bookinglistdata.bookings[j].status === 'PAID' || bookinglistdata.bookings[j].status === 'COMPLETE')) {
                        completeCount = completeCount + 1;
                    }
                    if (months[i] === months[tDate.getMonth()] && bookinglistdata.bookings[j].status === 'CANCELLED') {
                        cancelCount = cancelCount + 1;
                    }
                }
                allCompleteCount.push(completeCount);
                allCancelCount.push(cancelCount);
            }
        }
        setAllCompleteCount(allCompleteCount);
        setAllCancelCount(allCancelCount);
    }, [bookinglistdata.bookings]);

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <div style={{ padding: '24px', backgroundColor: currentTheme.background, minHeight: '100vh', transition: 'background-color 0.3s ease' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                    <h6 style={{ fontWeight: 600, marginBottom: 0, color: currentTheme.textMain, fontSize: '24px', transition: 'color 0.3s ease' }}>{t('gross_earning')}</h6>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px', marginBottom: '24px' }}>
                    {/* Daily Gross */}
                    <div style={{ border: currentTheme.cardBorder, borderRadius: '20px', height: '100%', background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'box-shadow 0.3s, transform 0.3s', boxShadow: currentTheme.shadow }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 30px rgba(72, 127, 255, 0.3)' : '0 10px 20px rgba(72, 127, 255, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                <div>
                                    <p style={{ fontWeight: 500, color: currentTheme.textMuted, marginBottom: '4px', fontSize: '14px', transition: 'color 0.3s ease' }}>{t('today_text')}</p>
                                    <h6 style={{ marginBottom: 0, color: currentTheme.textMain, fontSize: '28px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>
                                        {settings.swipe_symbol === false ? settings.symbol + ' ' + dailygross : dailygross + ' ' + settings.symbol}
                                    </h6>
                                </div>
                                <div style={{ width: '56px', height: '56px', background: 'rgba(72, 127, 255, 0.1)', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'all 0.3s ease', flexShrink: 0 }}>
                                    <CalendarTodayIcon sx={{ color: '#487FFF', fontSize: '26px' }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Monthly Gross */}
                    <div style={{ border: currentTheme.cardBorder, borderRadius: '20px', height: '100%', background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'box-shadow 0.3s, transform 0.3s', boxShadow: currentTheme.shadow }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 30px rgba(168, 85, 247, 0.3)' : '0 10px 20px rgba(168, 85, 247, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                <div>
                                    <p style={{ fontWeight: 500, color: currentTheme.textMuted, marginBottom: '4px', fontSize: '14px', transition: 'color 0.3s ease' }}>{t('this_month_text')}</p>
                                    <h6 style={{ marginBottom: 0, color: currentTheme.textMain, fontSize: '28px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>
                                        {settings.swipe_symbol === false ? settings.symbol + ' ' + monthlygross : monthlygross + ' ' + settings.symbol}
                                    </h6>
                                </div>
                                <div style={{ width: '56px', height: '56px', background: 'rgba(168, 85, 247, 0.1)', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'all 0.3s ease', flexShrink: 0 }}>
                                    <CalendarMonthIcon sx={{ color: '#a855f7', fontSize: '26px' }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Total Gross */}
                    <div style={{ border: currentTheme.cardBorder, borderRadius: '20px', height: '100%', background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'box-shadow 0.3s, transform 0.3s', boxShadow: currentTheme.shadow }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 30px rgba(16, 185, 129, 0.3)' : '0 10px 20px rgba(16, 185, 129, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                <div>
                                    <p style={{ fontWeight: 500, color: currentTheme.textMuted, marginBottom: '4px', fontSize: '14px', transition: 'color 0.3s ease' }}>{t('total')}</p>
                                    <h6 style={{ marginBottom: 0, color: currentTheme.textMain, fontSize: '28px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>
                                        {settings.swipe_symbol === false ? settings.symbol + ' ' + totalgross : totalgross + ' ' + settings.symbol}
                                    </h6>
                                </div>
                                <div style={{ width: '56px', height: '56px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', transition: 'all 0.3s ease', flexShrink: 0 }}>
                                    <MonetizationOnIcon sx={{ color: '#22c55e', fontSize: '26px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '24px', alignItems: 'stretch' }}>
                    {/* Map Section */}
                    <div style={{ flex: '3 1 480px' }}>
                        <div style={{ height: '100%', borderRadius: '20px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s' }}>
                            <div style={{ padding: '24px' }}>
                                <h6 style={{ fontSize: '18px', marginBottom: '16px', fontWeight: 600, color: currentTheme.textMain, transition: 'color 0.3s ease' }}>{t('real_time_driver_section_text')}</h6>
                                {mylocation && mylocation.lat ?
                                    <GoogleMap
                                        zoom={10}
                                        center={mylocation}
                                        mapContainerStyle={{ height: `450px`, borderRadius: '15px' }}
                                        options={{
                                            styles: currentTheme.mapStyles
                                        }}
                                    >
                                        {locations.map(marker => (
                                            <Marker
                                                position={{ lat: marker.lat, lng: marker.lng }}
                                                key={marker.id}
                                                icon={{
                                                    url: marker.carImage,
                                                    scaledSize: new window.google.maps.Size(40, 28)
                                                }}
                                            >
                                                <InfoWindow
                                                    position={{ lat: marker.lat, lng: marker.lng }}
                                                    options={{ pixelOffset: new window.google.maps.Size(0, -35) }}
                                                >
                                                    <div style={{ color: '#000' }}>{marker.drivername}<br />{marker.carnumber}</div>
                                                </InfoWindow>
                                            </Marker>
                                        ))}
                                    </GoogleMap>
                                    : null}
                            </div>
                        </div>
                    </div>

                    {/* User Stats Side Cards */}
                    <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {/* Customers */}
                        <div style={{ flex: '1 1 0', borderRadius: '20px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 25px rgba(72, 127, 255, 0.3)' : '0 10px 20px rgba(72, 127, 255, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                                <h6 style={{ marginBottom: '8px', fontWeight: 600, fontSize: '18px', color: currentTheme.textMain, transition: 'color 0.3s ease' }}>{t('total_cumtomer')}</h6>
                                <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: currentTheme.textMain, transition: 'color 0.3s ease', margin: 0 }}>{customerCount}</h2>
                            </div>
                        </div>

                        {/* Drivers */}
                        <div style={{ flex: '1 1 0', borderRadius: '20px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 25px rgba(239, 68, 68, 0.3)' : '0 10px 20px rgba(239, 68, 68, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                                <h6 style={{ marginBottom: '8px', fontWeight: 600, fontSize: '18px', color: currentTheme.textMain, transition: 'color 0.3s ease' }}>{t('total_drivers')}</h6>
                                <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: currentTheme.textMain, transition: 'color 0.3s ease', margin: 0 }}>{driverCount}</h2>
                            </div>
                        </div>

                        {/* Active Drivers */}
                        <div style={{ flex: '1 1 0', borderRadius: '20px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.boxShadow = isDark ? '0 0 25px rgba(34, 197, 94, 0.3)' : '0 10px 20px rgba(34, 197, 94, 0.15)'} onMouseLeave={(e) => e.currentTarget.style.boxShadow = currentTheme.shadow}>
                            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', textAlign: 'center' }}>
                                <h6 style={{ marginBottom: '8px', fontWeight: 600, fontSize: '18px', color: currentTheme.textMain, transition: 'color 0.3s ease' }}>{t('active_driver')}</h6>
                                <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: currentTheme.textMain, transition: 'color 0.3s ease', margin: 0 }}>{activeCount}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chart Section */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '24px', alignItems: 'stretch' }}>
                    {/* Area Chart - Booking History Trend */}
                    <div style={{ flex: '2 1 480px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, borderRadius: '20px', background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '24px', flexGrow: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <h6 style={{ fontSize: '18px', fontWeight: 600, color: currentTheme.textMain, margin: 0, transition: 'color 0.3s ease' }}>{t('booking_chart')}</h6>
                                <SegmentedControl 
                                    options={[
                                        { label: 'Area', value: 'area' },
                                        { label: 'Line', value: 'line' },
                                        { label: 'Bar', value: 'bar' }
                                    ]}
                                    value={trendChartType}
                                    onChange={setTrendChartType}
                                    isDark={isDark}
                                />
                            </div>
                            <Chart
                                options={barChartOptions}
                                series={barChart}
                                type={trendChartType}
                                width="100%"
                                height={320}
                            />
                        </div>
                    </div>

                    {/* Donut Chart - Booking Ratio for Selected Month */}
                    <div style={{ flex: '1 1 320px', border: currentTheme.cardBorder, boxShadow: currentTheme.shadow, borderRadius: '20px', background: currentTheme.cardBg, backdropFilter: 'blur(12px)', transition: 'all 0.3s', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '24px', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '8px' }}>
                                <h6 style={{ fontSize: '18px', fontWeight: 600, color: currentTheme.textMain, margin: 0, transition: 'color 0.3s ease' }}>{t('booking_ratio') === 'booking_ratio' ? 'Booking Ratio' : t('booking_ratio')}</h6>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <SegmentedControl 
                                        options={[
                                            { label: 'Donut', value: 'donut' },
                                            { label: 'Pie', value: 'pie' }
                                        ]}
                                        value={ratioChartType}
                                        onChange={setRatioChartType}
                                        isDark={isDark}
                                    />
                                    <CustomSelect 
                                        value={selectedMonth} 
                                        onChange={setSelectedMonth}
                                        options={['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, idx) => ({ label: m, value: idx }))}
                                        isDark={isDark}
                                        cardBorder={currentTheme.cardBorder}
                                    />
                                </div>
                            </div>
                            <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                {donutSeries[0] === 0 && donutSeries[1] === 0 ? (
                                    <div style={{ color: currentTheme.textMuted, fontSize: '14px', textAlign: 'center' }}>No bookings this month</div>
                                ) : (
                                    <Chart
                                        options={donutChartOptions}
                                        series={donutSeries}
                                        type={ratioChartType}
                                        width="100%"
                                        height={300}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    )
}

export default Dashboard;